<template>
  <div v-if="show" class="modal-overlay" @click.self="onClose">
    <div class="modal-container">
      <div class="modal-header">
        <h2>{{ mode === 'edit' ? 'Edit Row' : 'Add Row' }} — {{ configLabel }}</h2>
        <button class="close-btn" @click="onClose">✕</button>
      </div>
      <div class="modal-body">
        <p v-if="upsertNote && mode === 'add'" class="info-note">{{ upsertNote }}</p>
        <div v-if="formError" class="form-error">{{ formError }}</div>
        <div v-for="(group, gIdx) in fieldGroups" :key="gIdx" class="form-section">
          <h3 v-if="group.title" class="section-title">{{ group.title }}</h3>
          <div class="form-grid">
            <div v-for="field in group.fields" :key="field.name" class="form-field" :class="{ 'field-wide': field.type === 'textarea' }">
              <label class="field-label" :for="'rcf-' + field.name">
                {{ field.label }}<span v-if="field.required" class="required-mark">*</span>
              </label>
              <textarea
                v-if="field.type === 'textarea'"
                :id="'rcf-' + field.name"
                v-model="form[field.name]"
                class="field-input"
                rows="3"
                :placeholder="field.placeholder || ''"
                @input="fieldErrors[field.name] = null"
              ></textarea>
              <select
                v-else-if="field.type === 'select'"
                :id="'rcf-' + field.name"
                v-model="form[field.name]"
                class="field-input"
                @change="fieldErrors[field.name] = null"
              >
                <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
              </select>
              <label v-else-if="field.type === 'boolean'" class="checkbox-label" :for="'rcf-' + field.name">
                <input
                  :id="'rcf-' + field.name"
                  type="checkbox"
                  v-model="form[field.name]"
                  class="field-checkbox"
                />
                <span>{{ form[field.name] ? 'Yes' : 'No' }}</span>
              </label>
              <input
                v-else
                :id="'rcf-' + field.name"
                v-model="form[field.name]"
                class="field-input"
                :type="field.type === 'month' ? 'month' : field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'"
                :step="field.type === 'number' ? 'any' : null"
                :placeholder="field.placeholder || ''"
                :disabled="mode === 'edit' && field.keyField"
                @input="fieldErrors[field.name] = null"
              />
              <p v-if="field.help" class="field-help">{{ field.help }}</p>
              <p v-if="fieldErrors[field.name]" class="field-error">{{ fieldErrors[field.name] }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="cancel-btn" @click="onClose" :disabled="saving">Cancel</button>
        <button class="save-btn" @click="onSave" :disabled="saving || !isDirty">
          {{ saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Row' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { validateFieldValue } from '@/views/billing/recon-config-fields.js';

export default {
  name: 'ReconConfigRowModal',
  props: {
    show: { type: Boolean, default: false },
    configKey: { type: String, required: true },
    configLabel: { type: String, default: '' },
    fields: { type: Array, required: true },
    groups: { type: Array, default: null },
    upsertNote: { type: String, default: '' },
    mode: { type: String, default: 'add' },
    initialRow: { type: Object, default: null }
  },
  emits: ['save', 'close'],
  data: function() {
    return {
      form: {},
      fieldErrors: {},
      formError: '',
      saving: false
    };
  },
  computed: {
    fieldGroups: function() {
      if (this.groups && this.groups.length > 0) {
        var byName = {};
        for (var i = 0; i < this.fields.length; i++) byName[this.fields[i].name] = this.fields[i];
        return this.groups.map(function(g) {
          return {
            title: g.title,
            fields: g.fields.map(function(n) { return byName[n]; }).filter(Boolean)
          };
        });
      }
      return [{ title: '', fields: this.fields }];
    },
    isDirty: function() {
      if (this.mode === 'add') {
        for (var i = 0; i < this.fields.length; i++) {
          var f = this.fields[i];
          var v = this.form[f.name];
          var dflt = f.default !== undefined ? f.default : (f.type === 'boolean' ? false : '');
          if (f.type === 'boolean' ? v !== dflt : String(v === undefined || v === null ? '' : v) !== String(dflt)) return true;
        }
        return false;
      }
      if (!this.initialRow) return false;
      for (var j = 0; j < this.fields.length; j++) {
        var ff = this.fields[j];
        var cur = this.normalise(ff, this.form[ff.name]);
        var orig = this.normalise(ff, this.initialRow[ff.name]);
        if (cur !== orig) return true;
      }
      return false;
    }
  },
  watch: {
    show: function(val) {
      if (val) this.initForm();
    }
  },
  methods: {
    initForm: function() {
      var form = {};
      for (var i = 0; i < this.fields.length; i++) {
        var f = this.fields[i];
        if (this.mode === 'edit' && this.initialRow) {
          var raw = this.initialRow[f.name];
          form[f.name] = this.fromRowValue(f, raw);
        } else if (f.type === 'boolean') {
          form[f.name] = f.default !== undefined ? f.default : false;
        } else {
          form[f.name] = f.default !== undefined ? f.default : '';
        }
      }
      this.form = form;
      this.fieldErrors = {};
      this.formError = '';
      this.saving = false;
      var self = this;
      this.$nextTick(function() {
        var first = self.$el && self.$el.querySelector('.field-input:not([disabled])');
        if (first) first.focus();
      });
    },
    fromRowValue: function(field, raw) {
      if (field.type === 'boolean') return raw === true || raw === 'true' || raw === 1 || raw === 't';
      if (raw === null || raw === undefined) return '';
      if (field.type === 'month') return String(raw).slice(0, 7);
      if (field.type === 'date') return String(raw).slice(0, 10);
      return String(raw);
    },
    normalise: function(field, value) {
      if (field.type === 'boolean') return value ? 'true' : 'false';
      if (value === null || value === undefined) return '';
      return String(value);
    },
    onKeydown: function(event) {
      if (event.key === 'Escape') this.onClose();
    },
    onClose: function() {
      if (this.saving) return;
      this.$emit('close');
    },
    onSave: function() {
      var errors = {};
      for (var i = 0; i < this.fields.length; i++) {
        var f = this.fields[i];
        var err = validateFieldValue(f, this.form[f.name]);
        if (err) errors[f.name] = err;
      }
      this.fieldErrors = errors;
      if (Object.keys(errors).length > 0) {
        this.formError = 'Please fix the highlighted fields.';
        return;
      }
      this.formError = '';
      var payload = this.buildPayload();
      this.saving = true;
      this.$emit('save', {
        payload: payload,
        done: this.onSaveDone,
        fail: this.onSaveFailed
      });
    },
    buildPayload: function() {
      var payload = {};
      for (var i = 0; i < this.fields.length; i++) {
        var f = this.fields[i];
        var v = this.form[f.name];
        if (this.mode === 'edit') {
          var orig = this.normalise(f, this.initialRow ? this.initialRow[f.name] : null);
          var cur = this.normalise(f, v);
          if (cur === orig) continue;
          if (f.type === 'boolean') {
            payload[f.name] = v === true;
          } else if (v === '' || v === null || v === undefined) {
            payload[f.name] = null;
          } else if (f.type === 'number') {
            payload[f.name] = Number(v);
          } else {
            payload[f.name] = v;
          }
        } else {
          if (f.type === 'boolean') {
            payload[f.name] = v === true;
          } else if (v === '' || v === null || v === undefined) {
            if (f.required) payload[f.name] = v;
          } else if (f.type === 'number') {
            payload[f.name] = Number(v);
          } else {
            payload[f.name] = v;
          }
        }
      }
      return payload;
    },
    onSaveDone: function() {
      this.saving = false;
      this.$emit('close');
    },
    onSaveFailed: function(message, target) {
      this.saving = false;
      if (target) {
        var mapped = null;
        for (var i = 0; i < this.fields.length; i++) {
          if (this.fields[i].name === target) { mapped = target; break; }
        }
        if (mapped) {
          var e = {};
          e[mapped] = message;
          this.fieldErrors = Object.assign({}, this.fieldErrors, e);
          this.formError = 'Please fix the highlighted field.';
          return;
        }
      }
      this.formError = message || 'Save failed.';
    }
  },
  mounted: function() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount: function() {
    document.removeEventListener('keydown', this.onKeydown);
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal-container {
  background: #fff; border-radius: 12px; width: 760px; max-width: 95vw;
  max-height: 90vh; display: flex; flex-direction: column;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #e2e8f0;
}
.modal-header h2 { font-size: 0.9375rem; font-weight: 700; color: #0f172a; margin: 0; }
.close-btn {
  background: none; border: none; font-size: 1rem; cursor: pointer; color: #64748b;
}
.modal-body { padding: 20px; overflow-y: auto; }
.form-section { margin-bottom: 8px; }
.section-title {
  font-size: 0.75rem; font-weight: 700; color: #475569; text-transform: uppercase;
  letter-spacing: 0.04em; margin: 12px 0 8px 0;
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
.form-field { display: flex; flex-direction: column; }
.field-wide { grid-column: 1 / -1; }
.field-label { font-size: 0.75rem; font-weight: 600; color: #334155; margin-bottom: 4px; }
.required-mark { color: #dc2626; margin-left: 2px; }
.field-input {
  border: 1px solid #cbd5e1; border-radius: 6px; padding: 6px 10px;
  font-size: 0.8125rem; font-family: 'Inter', sans-serif;
}
.field-input:focus { outline: none; border-color: #4f46e5; box-shadow: 0 0 0 2px #e0e7ff; }
.field-input:disabled { background: #f1f5f9; color: #64748b; }
.checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 0.8125rem; color: #334155; }
.field-checkbox { width: 16px; height: 16px; }
.field-help { font-size: 0.7rem; color: #94a3b8; margin: 4px 0 0 0; }
.field-error { font-size: 0.7rem; color: #b91c1c; margin: 4px 0 0 0; }
.form-error {
  background: #fef2f2; border: 1px solid #fecaca; color: #991b1b;
  padding: 10px 14px; border-radius: 8px; font-size: 0.8125rem; margin-bottom: 12px;
}
.info-note {
  font-size: 0.8125rem; color: #6366f1; background: #eef2ff; border: 1px solid #c7d2fe;
  padding: 10px 14px; border-radius: 8px; margin-bottom: 12px;
}
.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 14px 20px; border-top: 1px solid #e2e8f0;
}
.cancel-btn, .save-btn {
  border: 1px solid #e2e8f0; padding: 7px 18px; border-radius: 8px;
  font-size: 0.8125rem; font-weight: 600; cursor: pointer;
}
.cancel-btn { background: #f1f5f9; color: #64748b; }
.save-btn { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.save-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
